import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChild,
} from '@angular/core';
import { ButtonComponent, ButtonSize, ButtonVariant } from '../button/button.component';
import { IconName } from '../icon/icons';

/** Tipi d'immagine accettati dalla foto del profilo. */
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Scelta di un file con l'aspetto di un pulsante della libreria. Il campo `<input type="file">`
 * resta nascosto alla vista e alla tastiera: si apre dal pulsante, che ha l'etichetta. Con
 * `capture` sul telefono si apre direttamente la fotocamera (`user` frontale, `environment`
 * posteriore). Il file scelto arriva da `picked`; si può scegliere di nuovo lo stesso file.
 */
@Component({
  selector: 'bs-file-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  host: { class: 'inline-flex' },
  template: `
    <input
      #control
      type="file"
      class="sr-only"
      tabindex="-1"
      aria-hidden="true"
      [attr.accept]="accept()"
      [attr.capture]="capture()"
      [disabled]="disabled()"
      [attr.data-testid]="testId() ? testId() + '-input' : null"
      (change)="onChange($event)"
    />
    <bs-button
      [variant]="variant()"
      [size]="size()"
      [icon]="icon()"
      [disabled]="disabled()"
      [fullWidth]="fullWidth()"
      [testId]="testId()"
      (click)="open()"
    >
      {{ label() }}
    </bs-button>
  `,
})
export class FilePickerComponent {
  readonly label = input('Scegli un file');
  readonly accept = input<string>(IMAGE_ACCEPT);
  /** Fotocamera del telefono: `user` (frontale) o `environment` (posteriore). */
  readonly capture = input<'user' | 'environment' | null>(null);
  readonly icon = input<IconName | null>('upload');
  readonly variant = input<ButtonVariant>('secondary');
  readonly size = input<ButtonSize>('md');
  readonly disabled = input(false);
  readonly fullWidth = input(false);
  readonly testId = input<string | null>(null);
  /** Il file scelto. */
  readonly picked = output<File>();

  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');

  /** Apre la finestra di scelta del file. */
  open(): void {
    if (!this.disabled()) {
      this.control().nativeElement.click();
    }
  }

  protected onChange(event: Event): void {
    const element = event.target as HTMLInputElement;
    const file = element.files?.[0];
    // Svuotato, così scegliere di nuovo lo stesso file genera un nuovo evento.
    element.value = '';
    if (file) {
      this.picked.emit(file);
    }
  }
}
